import { getCompanyGetCompany, getUserGetMyCompanies, postCompanyStatsQueryCompanyStats } from '@apis/Customers';
import { Company, CompanyStat } from '@apis/Customers/model';
import styled from '@emotion/styled';
import { Anchor, Box, Center, Divider, Drawer, Loader, Popover, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { confirmAction } from '@root/Components/CompanyInfo/Administration/ConfirmActionDialog';
import { Picker } from '@root/Components/Picker/Picker';
import { CompanyTenantPrereqService, useCompany } from '@root/Components/Router/CompanyContent';
import { CompanyContext } from '@root/Components/Router/CompanyContext';
import { useGetDefaultPageByCompany } from '@root/Components/Router/Hooks';
import { SiteMenuCompanyService } from '@root/Components/Shell/SiteMenuCompanyService';
import { PageContent, PagePanel, PanelBody, PaneledPage } from '@root/Design/Layout';
import { CompanyContextService } from '@root/Services/Customers/CompanyContext';
import { useDi } from '@root/Services/DI';
import { EventEmitter, useEvent, useEventValue } from '@root/Services/EventEmitter';
import { MspService } from '@root/Services/MspService';
import { useNav } from '@root/Services/NavigationService';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { queryBuilder } from '@root/Services/QueryExpr';
import { IChildRouteReady, useEndpointUnloading } from '@root/Services/Router/BasicRouteLoader';
import { endpoint } from '@root/Services/Router/EndpointRegistry';
import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { ChevronDown } from 'tabler-icons-react';
import { singleton } from 'tsyringe';
import { AdminSidePanel } from '../../Components/CompanyInfo/Administration/AdminSidePanel';
import { MyCompaniesGrid } from './MyCompaniesGrid';
import { IdleResourcesService } from '../../Services/Resources/IdleResourcesService';

@singleton()
export class AdminSidePanelChangeContext {
    public sidePanelHasChange = new EventEmitter(false);
}

export const MyCompanies = () => {
    const [showSidePanel, setshowSidePanel] = useState(false);
    const [sidePanelDefaultTab, setSidePanelDefaultTab] = useState<string>();
    const [companyDeleted, setCompanyDeleted] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number>();
    const company = useCompany();
    const contextSvc = useDi(CompanyContextService);
    const mspCompany = contextSvc.parentCompany;
    const notificationSvc = useDi(NotificationService);
    const mspSvc = useDi(MspService);
    const nav = useNav();
    const [panelCloseInitiated, setPanelCloseInitiated] = useState<boolean>(false);
    const adminSidePanelChangeCtx = useDi(AdminSidePanelChangeContext);
    const editing = useEventValue(adminSidePanelChangeCtx.sidePanelHasChange);
    const [myUserCompanies, setMyUserCompanies] = useState<Company[]>();

    useEffect(() => {
        getUserGetMyCompanies().then(setMyUserCompanies);
    }, []);

    useEvent(
        mspSvc.companyListChanged,
        useCallback(() => getUserGetMyCompanies().then(setMyUserCompanies), [])
    );

    const onCompanyRowInfoNeeded = useCallback(
        (companyStat: CompanyStat) => {
            if (mspCompany?.Type === 'Msp') {
                if (myUserCompanies?.find((c) => c.Id === companyStat.Id)?.Id) {
                    return undefined;
                } else return 'noLink' as const;
            }
            return undefined;
        },
        [myUserCompanies]
    );

    const onUnloadCompany = useCallback(() => {
        setSelectedCompanyId(0);
    }, [setSelectedCompanyId]);

    const handleToggleSidePanel = (showSidePanel: boolean) => {
        setshowSidePanel(showSidePanel);
    };

    const handleToggleCompanyDeleted = (companyDeleted: boolean) => {
        setCompanyDeleted(companyDeleted);
    };

    const selectCompany = (selectedCompanyId: number): void => {
        if (selectedCompanyId && selectedCompanyId > 0) {
            setSelectedCompanyId(selectedCompanyId);
            handleToggleSidePanel(true);
        }
    };

    useEffect(() => {
        if (showSidePanel && editing) {
            confirmAction({
                headerText: 'Close Panel',
                promptText: `You have unsaved changes. Are you sure you want to close?`,
                confirmButtonText: 'Discard Changes and Close',
                cancelButtonText: 'Save Changes',
                confirmAction: () => {
                    setshowSidePanel(false);
                    setPanelCloseInitiated(false);
                },
            });
        } else {
            setshowSidePanel(false);
            setPanelCloseInitiated(false);
        }
    }, [panelCloseInitiated]);

    useEffect(() => {
        const { select: rawId, tab } = nav.getData('select', 'tab');
        const childIdNum = rawId ? parseInt(rawId) : '';
        if (childIdNum) {
            setSelectedCompanyId(childIdNum);
            setshowSidePanel(true);
            setSidePanelDefaultTab(tab);
        }
    }, []);

    useEffect(() => {
        if (companyDeleted) {
            notificationSvc.notify(
                'Company deleted successfully.',
                '',
                'success',
                <>
                    <i className="ti ti-circle-check"></i>
                </>
            );
            mspSvc.notifyCompanyListChanged();
            setCompanyDeleted(false);
        }
    }, [companyDeleted]);

    return (
        <PageContent>
            <PaneledPage>
                <PagePanel size="fill">
                    <PanelBody>
                        {myUserCompanies && (
                            <MyCompaniesGrid selectCompany={selectCompany} onCompanyRowInfoNeeded={onCompanyRowInfoNeeded}></MyCompaniesGrid>
                        )}
                    </PanelBody>
                </PagePanel>
                <Divider orientation="vertical" />

                {showSidePanel && (
                    <Drawer
                        opened={showSidePanel}
                        onClose={() => setPanelCloseInitiated(true)}
                        size={900}
                        padding={0}
                        position="right"
                        overlayOpacity={0.1}
                        withinPortal={false}
                        withCloseButton={false}
                        withOverlay={true}
                        shadow="md"
                        closeOnClickOutside={true}
                        closeOnEscape={true}
                    >
                        <CompanyContext unloadOnUnmount companyId={selectedCompanyId!} type="company" onUnload={onUnloadCompany}>
                            {() => (
                                <PagePanel padded={false} size="md" style={{ minWidth: '350px', width: '100%', height: '100%' }}>
                                    <AdminSidePanel
                                        defaultTab={sidePanelDefaultTab}
                                        parentCompany={company!}
                                        toggleSidePanel={handleToggleSidePanel}
                                        toggleCompanyDeleted={handleToggleCompanyDeleted}
                                    />
                                </PagePanel>
                            )}
                        </CompanyContext>
                    </Drawer>
                )}
            </PaneledPage>
        </PageContent>
    );
};
endpoint('my-companies', MyCompanies, 'My Companies');

export function ManageCompanyShim() {
    const { getData } = useNav();
    const { id: rawId } = getData('id');
    const id = rawId ? parseInt(rawId) : null;

    const childReady = useDi(IChildRouteReady) as EventEmitter<boolean>;
    const siteMenuCompanySvc = useDi(SiteMenuCompanyService);

    useEndpointUnloading(
        useCallback(() => {
            siteMenuCompanySvc.company.emit(undefined);
        }, [])
    );
    const onCompanyLoaded = useCallback(
        (company?: Company) => {
            if (company) {
                if (!childReady.value || siteMenuCompanySvc.company.value?.Id !== company.Id) {
                    siteMenuCompanySvc.company.emit(company);
                    childReady.emit(true);
                }
            } else {
                childReady.emit(false);
                siteMenuCompanySvc.company.emit(undefined);
            }
        },
        [childReady, siteMenuCompanySvc]
    );
    return typeof id === 'number' ? (
        <CompanyContext key={id} type="company" companyId={id} onCompanyLoaded={onCompanyLoaded}>
            {() => <></>}
        </CompanyContext>
    ) : null;
}

function CompanyPicker({ onClose }: { onClose: () => void }) {
    const companyContextSvc = useDi(CompanyContextService);
    const [companies, setCompanies] = useState<CompanyStat[]>();
    const { goto } = useNav();
    const companyDefaultPageProvider = useGetDefaultPageByCompany();
    useEffect(() => {
        companyContextSvc.withParentCompany(async () => {
            queryBuilder<CompanyStat>()
                .where((c) => c.model.Id!.ne(companyContextSvc.parentCompany.Id!))
                .take(1000)
                .execute(postCompanyStatsQueryCompanyStats)
                .then((results) => {
                    const r = results.Results ?? [];
                    r.sort((a, b) => {
                        if (a.SubscriptionActive !== b.SubscriptionActive) {
                            return a.SubscriptionActive ? -1 : 1;
                        }
                        return (a.CompanyName ?? '').localeCompare(b.CompanyName ?? '', undefined, { sensitivity: 'base' });
                    });
                    setCompanies(r);
                });
        });
    }, []);
    const idleResourceService = useDi(IdleResourcesService);
    const onSelect = useCallback(async (companies: CompanyStat[]) => {
        const url = companies[0] ? await companyDefaultPageProvider(companies[0]) : null;
        if (url) {
            idleResourceService.invalidateCache();
            goto(url);
        }
        onClose();
    }, []);
    return (
        <PickerContainer>
            <Picker
                items={companies ?? []}
                mode="single"
                selections={[]}
                minimizeHeight
                height={400}
                width={400}
                renderItem={(item) => {
                    return (
                        <CompanyPickerItem>
                            <Text color="primary" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.CompanyName}
                            </Text>
                            <Box className="state">
                                <StatusIndicator status={item.SubscriptionActive ? 'Active' : 'Inactive'} />
                                {item.SubscriptionActive ? 'Active' : 'Inactive'}
                            </Box>
                        </CompanyPickerItem>
                    );
                }}
                nameAccessor={(c) => c.CompanyName ?? ''}
                onChange={onSelect}
            />
            {companies === undefined ? (
                <Center my="lg">
                    <Loader />
                </Center>
            ) : null}
        </PickerContainer>
    );
}

const PickerContainer = styled.div`
    .picker-item {
        border-bottom: solid 1px ${(p) => p.theme.colors.gray[3]};
        padding: 0;
        cursor: pointer;
        > div {
            border-radius: 0;
            padding: 0;
            height: 100%;
        }
        :hover > div {
            background: ${(p) => p.theme.colors.primary[1]};
        }
    }
    .picker-item-text {
        flex: 1;
    }
`;
const StatusIndicator = styled.div<{ status: 'Active' | 'Inactive' }>`
    width: 10px;
    height: 10px;
    border-radius: 10px;
    margin-right: 5px;
    background: ${(p) => (p.status === 'Active' ? p.theme.colors.success[6] : p.theme.colors.gray[4])};
`;
const CompanyPickerItem = styled.div`
    display: grid;
    grid-template-columns: auto 100px;
    width: 100%;
    .state {
        justify-self: center;
        display: flex;
        align-items: center;
    }
`;

function ManageCompanyBreadCrumb() {
    const { getData } = useNav();
    const { id: rawId } = getData('id');
    const id = rawId ? parseInt(rawId) : null;
    const [customer, setCustomer] = useState<Company | null | undefined>();
    const [pickerOpen, { close, open, toggle }] = useDisclosure(false);
    const tenantSvc = useDi(CompanyTenantPrereqService);

    useEffect(() => {
        if (id) {
            tenantSvc.getCompanyById(id).then(setCustomer);
        }
    }, [id]);
    return (
        <>
            <Popover opened={pickerOpen} position="bottom-start" closeOnClickOutside onClose={close} shadow="lg" withArrow withinPortal>
                <Popover.Target>
                    <Anchor sx={{ display: 'flex' }} onClick={toggle}>
                        <Text mr={4}>{customer === undefined ? 'Loading' : customer === null ? 'Invalid' : customer.CompanyName}</Text>
                        <ChevronDown />
                    </Anchor>
                </Popover.Target>
                <Popover.Dropdown p={0}>{pickerOpen ? <CompanyPicker onClose={close} /> : null}</Popover.Dropdown>
            </Popover>
        </>
    );
}
ManageCompanyBreadCrumb.noLink = true;

endpoint('manage-company', ManageCompanyShim, ManageCompanyBreadCrumb, true);
